import { Link } from "react-router-dom";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Logo from "@/components/app/Logo";
import LangCountrySelector from "@/components/app/LangCountrySelector";
import VerifiedBadge from "@/components/app/VerifiedBadge";
import { PartnerLeadsCounter } from "@/components/app/PartnerLeadsCounter";
import { Search, MapPin, ShieldCheck, Users, MessageSquare, Briefcase, GraduationCap, ThumbsUp, Heart, Truck, Pill } from "lucide-react";
import { COUNTRIES } from "@/lib/i18n";

export default function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");

  const onSearch = (e) => {
    e?.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (location) p.set("location", location);
    navigate(`/dashboard/search?${p.toString()}`);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Nav */}
      <nav className="border-b border-slate-200 bg-white/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="logo-link"><Logo h={36} /></Link>
          <div className="hidden md:flex items-center gap-7 text-sm font-semibold text-slate-700">
            <Link to="/register?role=doctor" className="hover:text-[#618DB8]" data-testid="nav-for-pros">{t("nav.for_pros")}</Link>
            <Link to="/register?role=pharmacy" className="hover:text-[#618DB8]" data-testid="nav-for-pharmacies">{t("nav.for_pharmacies")}</Link>
            <Link to="/partenaires" className="hover:text-[#618DB8]" data-testid="nav-partners">Devenir partenaire</Link>
            <Link to="/tarifs" className="hover:text-[#618DB8]" data-testid="nav-pricing">Tarifs</Link>
            <a href="#features" className="hover:text-[#618DB8]">{t("nav.how")}</a>
          </div>
          <div className="flex items-center gap-2">
            <LangCountrySelector compact />
            <Link to="/login" data-testid="nav-login-link"><Button variant="ghost" className="text-slate-700 font-semibold">{t("nav.login")}</Button></Link>
            <Link to="/register" data-testid="nav-register-link"><Button className="btn-primary">{t("nav.register")}</Button></Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#618DB8] via-[#4A78A3] to-[#2A4365]">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-32 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur text-white text-xs font-bold px-3 py-1.5 rounded-full mb-6 border border-white/20">
              <Heart className="h-3.5 w-3.5 fill-current" />
              <span className="tracking-wider uppercase">{t("brand.tagline")}</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
              {t("hero.title_1")}<br />
              <span className="font-brush text-5xl sm:text-6xl lg:text-7xl text-[#FFB89F] font-medium tracking-wide">{t("hero.title_2")}</span>
            </h1>
            <p className="mt-6 text-lg text-white/90 max-w-2xl leading-relaxed">{t("hero.subtitle")}</p>

            <div className="mt-8 flex flex-wrap gap-2">
              {COUNTRIES.map((c) => (
                <span key={c.code} className="bg-white/15 text-white border border-white/20 rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1.5">
                  <span className="text-base leading-none">{c.flag}</span> {c.name}
                </span>
              ))}
            </div>
          </div>

          {/* Search bar */}
          <form onSubmit={onSearch} className="mt-10 bg-white rounded-2xl shadow-2xl shadow-blue-900/40 p-3 sm:p-4 grid grid-cols-1 lg:grid-cols-12 gap-2">
            <div className="lg:col-span-5 flex items-center gap-2 px-3 py-2 border-b lg:border-b-0 lg:border-e border-slate-200">
              <Search className="h-5 w-5 text-slate-400 flex-shrink-0" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Praticien, spécialité, condition…" className="border-0 shadow-none focus-visible:ring-0 px-0 h-10 text-sm font-medium" data-testid="hero-search-input" />
            </div>
            <div className="lg:col-span-4 flex items-center gap-2 px-3 py-2 border-b lg:border-b-0 lg:border-e border-slate-200">
              <MapPin className="h-5 w-5 text-slate-400 flex-shrink-0" />
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ville · Paris, Casa, Tunis, Dubai…" className="border-0 shadow-none focus-visible:ring-0 px-0 h-10 text-sm font-medium" data-testid="hero-location-input" />
            </div>
            <div className="lg:col-span-3 p-1">
              <Button type="submit" className="btn-coral w-full h-12 text-base font-bold rounded-xl" data-testid="hero-search-btn">
                <Search className="h-4 w-4 me-2" />{t("common.search")}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Verified badge spotlight */}
      <section className="max-w-5xl mx-auto px-6 -mt-16 relative z-10">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8 flex items-center gap-6 flex-wrap">
          <div className="w-16 h-16 rounded-2xl bg-[#618DB8] flex items-center justify-center">
            <ShieldCheck className="h-9 w-9 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl font-extrabold flex items-center text-deep">
              Le badge bleu Doctobook
              <VerifiedBadge size={18} />
            </div>
            <p className="text-slate-600 mt-1">Diplôme &amp; Ordre · RPPS · Vérification Identité — les trois piliers de la confiance santé.</p>
          </div>
          <Link to="/register?role=doctor">
            <Button className="btn-primary">Devenir vérifié</Button>
          </Link>
        </div>
      </section>

      {/* Two-column for patients vs pros */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="rounded-2xl border-2 border-slate-200 p-8 card-hover bg-gradient-to-br from-white to-slate-50">
            <Users className="h-10 w-10 text-[#FF7A5C]" />
            <h3 className="mt-4 font-display text-2xl font-extrabold text-deep">Pour les patients</h3>
            <p className="mt-2 text-slate-600">Plus qu'une prise de RDV : un compagnon de votre parcours de soin.</p>
            <ul className="mt-5 space-y-2.5 text-sm text-slate-700">
              <li className="flex items-start gap-2"><span className="text-[#FF7A5C] font-bold">→</span> Prise de rendez-vous + téléconsultation HIPAA</li>
              <li className="flex items-start gap-2"><span className="text-[#FF7A5C] font-bold">→</span> Suivi du parcours de soin et DMP partagé</li>
              <li className="flex items-start gap-2"><span className="text-[#FF7A5C] font-bold">→</span> Messagerie sécurisée avec votre praticien</li>
              <li className="flex items-start gap-2"><span className="text-[#FF7A5C] font-bold">→</span> Recommandations et avis vérifiés</li>
            </ul>
            <Link to="/register?role=patient" className="mt-6 inline-block"><Button className="btn-coral">{t("hero.cta_patient")}</Button></Link>
          </div>
          <div className="rounded-2xl border-2 border-[#618DB8] p-8 card-hover bg-gradient-to-br from-white to-[#618DB8]/5">
            <ShieldCheck className="h-10 w-10 text-[#618DB8]" />
            <h3 className="mt-4 font-display text-2xl font-extrabold text-deep flex items-center">Pour les professionnels<VerifiedBadge size={18} /></h3>
            <p className="mt-2 text-slate-600">L'espace santé où confrères, patients et pharmaciens se retrouvent en un seul lieu.</p>
            <ul className="mt-5 space-y-2.5 text-sm text-slate-700">
              <li className="flex items-start gap-2"><span className="text-[#618DB8] font-bold">→</span> Profil certifié + badge bleu vérifié</li>
              <li className="flex items-start gap-2"><span className="text-[#618DB8] font-bold">→</span> Réseau entre confrères, partage d'expertise</li>
              <li className="flex items-start gap-2"><span className="text-[#618DB8] font-bold">→</span> Recrutement et offres d'emploi entre membres</li>
              <li className="flex items-start gap-2"><span className="text-[#618DB8] font-bold">→</span> Formation continue certifiante</li>
              <li className="flex items-start gap-2"><span className="text-[#618DB8] font-bold">→</span> Agenda, ordonnances Vidal, téléconsultation HDS</li>
            </ul>
            <Link to="/register?role=doctor" className="mt-6 inline-block"><Button className="btn-primary">{t("hero.cta_doctor")}</Button></Link>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-4 gap-8 text-center">
          {[
            { icon: ShieldCheck, n: "3 niveaux", l: "de vérification professionnelle" },
            { icon: MessageSquare, n: "100% chiffré", l: "messagerie & téléconsult." },
            { icon: Briefcase, n: "Sans commission", l: "sur les recrutements" },
            { icon: GraduationCap, n: "Certifié", l: "formation continue" },
          ].map((s) => (
            <div key={s.l} className="flex flex-col items-center">
              <s.icon className="h-7 w-7 text-[#618DB8] mb-3" />
              <div className="font-display text-xl font-extrabold text-deep">{s.n}</div>
              <div className="mt-1 text-xs text-slate-600">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Pilot PACA — Doctobook Trans & Rx */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-orange-50 py-16 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide">
              <MapPin className="h-3 w-3" />Pilote PACA · Q2 2026
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#FF7A5C]">Programme partenaires</span>
          </div>
          <div className="grid md:grid-cols-[1.4fr_1fr] gap-8 items-center">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-deep tracking-tight leading-tight">
                Transporteurs sanitaires & pharmacies :<br />
                <span className="text-[#FF7A5C]">rejoignez le pilote</span> en région PACA.
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
                20 premières structures agréées sélectionnées pour tester Doctobook Trans (ambulances / VSL / taxis conventionnés) et Doctobook Rx (officines).
                Pré-inscription non engageante · réponse sous 5 jours.
              </p>
              <PartnerLeadsCounter />
            </div>
            <div className="grid gap-3">
              <Link to="/partenaires/pre-inscription?type=transport">
                <Button className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="landing-cta-transport">
                  <Truck className="h-4 w-4 me-2" />Doctobook Trans → Pré-inscription
                </Button>
              </Link>
              <Link to="/partenaires/pre-inscription?type=pharmacy">
                <Button className="w-full h-12 btn-coral font-bold" data-testid="landing-cta-pharmacy">
                  <Pill className="h-4 w-4 me-2" />Doctobook Rx → Pré-inscription
                </Button>
              </Link>
              <Link to="/partenaires" className="text-xs text-slate-500 hover:text-slate-700 text-center">
                Voir tous les programmes partenaires →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="bg-gradient-to-br from-[#2A4365] to-[#618DB8] text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Rejoignez le réseau social qui change la santé.</h2>
          <p className="mt-4 text-white/90 max-w-xl mx-auto">Présent en France, Maroc, Tunisie et Émirats arabes unis. Bientôt dans votre pays.</p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to="/register"><Button size="lg" className="btn-coral h-12 px-8">{t("nav.register")}</Button></Link>
            <Link to="/login"><Button size="lg" variant="ghost" className="text-white hover:bg-white/10 h-12 px-8">{t("nav.login")}</Button></Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row gap-4 justify-between items-center text-sm text-slate-500">
          <Logo h={28} />
          <div>© {new Date().getFullYear()} Doctobook · {t("brand.tagline")}</div>
        </div>
      </footer>
    </div>
  );
}
